import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  ChevronLeft,
  Search,
  Star,
  CalendarDays,
  FileText,
  Scale,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useT, type TranslationKey } from "@/lib/translation";
import { getUserSettings, updateUserSettings } from "@/api/client";

interface AnalysisStep {
  icon: typeof Search;
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  detailsKey: TranslationKey;
  image: string;
}

const STEPS: AnalysisStep[] = [
  {
    icon: Search,
    titleKey: "onboarding:analysisWelcome.steps.search.title",
    descriptionKey: "onboarding:analysisWelcome.steps.search.description",
    detailsKey: "onboarding:analysisWelcome.steps.search.details",
    image: "/guides/onboarding/Analysis/step1.png",
  },
  {
    icon: Star,
    titleKey: "onboarding:analysisWelcome.steps.watchlist.title",
    descriptionKey: "onboarding:analysisWelcome.steps.watchlist.description",
    detailsKey: "onboarding:analysisWelcome.steps.watchlist.details",
    image: "/guides/onboarding/Analysis/step2.png",
  },
  {
    icon: CalendarDays,
    titleKey: "onboarding:analysisWelcome.steps.earnings.title",
    descriptionKey: "onboarding:analysisWelcome.steps.earnings.description",
    detailsKey: "onboarding:analysisWelcome.steps.earnings.details",
    image: "/guides/onboarding/Analysis/step3.png",
  },
  {
    icon: FileText,
    titleKey: "onboarding:analysisWelcome.steps.transcripts.title",
    descriptionKey: "onboarding:analysisWelcome.steps.transcripts.description",
    detailsKey: "onboarding:analysisWelcome.steps.transcripts.details",
    image: "/guides/onboarding/Analysis/step4.png",
  },
  {
    icon: Scale,
    titleKey: "onboarding:analysisWelcome.steps.valuation.title",
    descriptionKey: "onboarding:analysisWelcome.steps.valuation.description",
    detailsKey: "onboarding:analysisWelcome.steps.valuation.details",
    image: "/guides/onboarding/Analysis/step5.png",
  },
];

interface AnalysisWelcomeModalProps {
  open?: boolean;
  onClose?: () => void;
}

export default function AnalysisWelcomeModal({
  open: controlledOpen,
  onClose: controlledOnClose,
}: AnalysisWelcomeModalProps = {}) {
  const t = useT();
  const isControlled = controlledOpen !== undefined;
  const [autoOpen, setAutoOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const open = isControlled ? controlledOpen : autoOpen;

  const queryClient = useQueryClient();
  const { data: userSettings } = useQuery({
    queryKey: ["userSettings"],
    queryFn: getUserSettings,
  });

  useEffect(() => {
    if (isControlled) return;
    if (!userSettings || userSettings.onboarding_analysis_seen) return;
    setAutoOpen(true);
    updateUserSettings({ onboarding_analysis_seen: true }).catch(() => {});
    queryClient.setQueryData(["userSettings"], {
      ...userSettings,
      onboarding_analysis_seen: true,
    });
  }, [isControlled, userSettings, queryClient]);

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;
  const Icon = step.icon;

  const handleClose = () => {
    if (isControlled) {
      controlledOnClose?.();
    } else {
      setAutoOpen(false);
    }
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-lg h-[700px] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl">
            {t("onboarding:analysisWelcome.title")}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding:analysisWelcome.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 overflow-hidden">
          {/* Step indicators */}
          <div className="flex gap-1 flex-shrink-0">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i === currentStep ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex-shrink-0 flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary shrink-0" />
              <div>
                <h2 className="text-lg font-semibold">{t(step.titleKey)}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t(step.descriptionKey)}
                </p>
              </div>
            </div>

            {/* Screenshot, centered with flexible spacing */}
            <div className="flex-1 flex items-center justify-center">
              <div className="rounded-lg border-2 border-muted-foreground/20 overflow-hidden flex items-center justify-center bg-background">
                <img
                  src={step.image}
                  alt={t(step.titleKey)}
                  className="max-w-full max-h-80 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            </div>

            {/* Details text */}
            <p className="text-sm text-muted-foreground leading-relaxed flex-shrink-0">
              {t(step.detailsKey)}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex-1 text-center text-xs text-muted-foreground">
              {t("onboarding:wizard.stepOf", {
                current: currentStep + 1,
                total: STEPS.length,
              })}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              disabled={isLastStep}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="ghost" onClick={handleClose} className="flex-1">
              {t("onboarding:wizard.skipIntro")}
            </Button>
            <Button onClick={handleNext} className="flex-1 rounded-2xl">
              {isLastStep
                ? t("onboarding:analysisWelcome.startExploring")
                : t("onboarding:wizard.next")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
